// useSyncLastProject — keep the cached `/me`'s last_project_id in step with the
// project the user is actually in, the instant they enter it.
//
// The backend records last_project_id on every project request (a fire-and-
// forget UPDATE in requireProjectAccess), but switching projects is a pure
// <Link> with NO /me refetch (the locked "switch = no API call" rule). So the
// cached /me's last_project_id would lag the URL until the next 60s refetch,
// and the `/` resolver could land on a stale (often first-visited) project.
//
// This reconciles the cache locally — a setQueryData write, NOT a mutation /
// API call / broadcast — so the resolver always sees the true last-visited
// project with zero network cost. The next real /me refetch confirms the same
// value the backend already persisted. Writing an unreachable id is harmless:
// resolveLanding re-validates last_project_id against availableProjects.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@ems-app/schemas/auth/responses';
import { ME_QUERY_KEY } from './useMe.js';

export function useSyncLastProject(projectId: string | null): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!projectId) return;
    queryClient.setQueryData<MeResponse>(ME_QUERY_KEY, (old) =>
      old && old.user.last_project_id !== projectId
        ? { ...old, user: { ...old.user, last_project_id: projectId } }
        : old,
    );
  }, [projectId, queryClient]);
}
